import { z } from "zod";
import type { TFunction } from "i18next";
import type { Interval } from "../types";

const intervalSchema = z.object({
    from: z.date().nullable().optional(),
    to: z.date().nullable().optional(),
});

const dayDataSchema = z.object({
    allDay: z.boolean(),
    isAvailable: z.boolean(),
    reason: z.string().optional(),
    intervals: z.array(intervalSchema),
});

export const createTemporarySchema = (t: TFunction, dates: string[]) => {
    const shape = Object.fromEntries(dates.map((day) => [day, dayDataSchema]));

    return z.object(shape).superRefine((values: any, ctx) => {
        dates.forEach((day) => {
            const dayData = values[day];
            if (!dayData || dayData.allDay) return;

            dayData.intervals?.forEach((interval: Interval, index: number) => {
                const prev = dayData.intervals[index - 1];
                const path = [day, "intervals", index];
                const from = interval.from as Date | undefined;
                const to = interval.to as Date | undefined;
                const prevFrom = prev?.from as Date | undefined;
                const prevTo = prev?.to as Date | undefined;

                if (from) {
                    if (to && from.getTime() >= to.getTime()) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, "from"], message: t("start-smaller-than-end") });
                    } else if (prevFrom && prevTo && from.getTime() > prevFrom.getTime() && from.getTime() < prevTo.getTime()) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, "from"], message: t("start-between-previous-start-and-end-time") });
                    } else if (prevFrom && from.getTime() === prevFrom.getTime()) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, "from"], message: t("start-same-as-previous-start-time") });
                    } else if (!to) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, "from"], message: t("required-end") });
                    }
                }

                if (to) {
                    if (from && to.getTime() <= from.getTime()) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, "to"], message: t("end-must-be-same-or-bigger-than-start-time") });
                    } else if (prevFrom && prevTo && to.getTime() > prevFrom.getTime() && to.getTime() < prevTo.getTime()) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, "to"], message: t("end-time-cannot-be-between-previous-start-and-end-time") });
                    } else if (from && prevFrom && prevTo && from.getTime() <= prevFrom.getTime() && to.getTime() >= prevTo.getTime()) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, "to"], message: t("shifts-cannot-overlap-each-other") });
                    } else if (!from) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, "to"], message: t("required-start") });
                    }
                }
            });
        });
    });
};
